import { useCallback } from 'react';
import { Button, IconPlus, Stack, Text } from '@adc/parallax-component-library';
import { createFileRoute, Outlet } from '@tanstack/react-router';

import type { AbbottUserSort, AdminUser, SortDirection } from '~/api/types';
import { AbbottUsersTable } from '~/components/table/abbottUsers/AbbottUsersTable';
import type { AbbottUsersTableColumnKey } from '~/components/table/abbottUsers/types';
import { USER_ROLES } from '~/features/auth/roles';
import type { CustomerAdminUsersTabsValue } from '~/features/customerAdministration/users/constants';
import { useModal } from '~/features/modals/useModal';
import type { AdminUsersSearchParams } from '~/types/AdminUsersRoute';
import { buildTestId } from '~/utils/buildTestId';
import { protectedRoute } from '~/utils/routesAuthorization';

const TEST_ID = 'Abbott-user-table';

export const Route = createFileRoute('/abbott-administration/users')({
  component: RouteComponent,
  validateSearch: (
    search,
  ): AdminUsersSearchParams<CustomerAdminUsersTabsValue, AbbottUserSort> => {
    // Validate and parse the search params into a typed state
    const defaultSortDir: SortDirection =
      search.sortDir === 'asc' ? 'asc' : 'desc';
    const sortDir = search.sortDir ? defaultSortDir : undefined;

    return {
      page: search.page ? Number(search.page) || 1 : undefined,
      sort: (search.sort as AbbottUserSort | undefined) ?? undefined,
      search:
        search.search && typeof search.search === 'string'
          ? decodeURI(search.search)
          : undefined,
      sortDir,
    };
  },
  beforeLoad: () => {
    protectedRoute({
      roles: [USER_ROLES.AdminAbbott, USER_ROLES.AbbottUser],
    });
  },
});

function RouteComponent() {
  const { page = 1, search = '', sort, sortDir = 'desc' } = Route.useSearch();
  const { openModal } = useModal();

  const navigate = Route.useNavigate();

  const handleSearch = async (searchValue: string) => {
    // Update the search term and reset to the first page
    await navigate({
      search: (prev) => ({ ...prev, page: 1, search: encodeURI(searchValue) }),
    });
  };

  const handlePageChange = async (newPage: number) => {
    // Update the page number in the search params
    await navigate({
      search: (prev) => ({ ...prev, page: newPage }),
    });
  };

  const handleSort = async (
    newSort: AbbottUsersTableColumnKey,
    newSortDir: SortDirection,
  ) => {
    // Update the sort column and direction in the search params
    await navigate({
      search: (prev) => ({
        ...prev,
        sort: newSort,
        sortDir: prev.sort === newSort && newSortDir === 'asc' ? 'desc' : 'asc',
      }),
    });
  };

  const onRowPress = useCallback(
    (user: AdminUser) => {
      void navigate({
        to: '/abbott-administration/users/profile/$userId',
        params: { userId: user.id },
      });
    },
    [navigate],
  );

  const onViewUserProfile = useCallback(
    (userId: string) =>
      navigate({
        to: '/abbott-administration/users/profile/$userId',
        params: { userId },
      }),
    [navigate],
  );

  return (
    <Stack height="auto" testID={TEST_ID}>
      <Text
        type="$h3.medium"
        marginBottom="$2"
        testID={buildTestId({ testID: TEST_ID, suffix: 'title' })}
      >
        Abbott User Administration
      </Text>
      <AbbottUsersTable
        testID={TEST_ID}
        page={page}
        search={search}
        sort={sort}
        sortDir={sortDir}
        onPageChange={handlePageChange}
        onSearch={handleSearch}
        onSortChange={handleSort}
        onRowPress={onRowPress}
        headerActions={
          <Button
            testID={buildTestId({
              testID: TEST_ID,
              suffix: ['add', 'user', 'button'],
            })}
            size="medium"
            variant="primary"
            label="Add user"
            iconStart={<IconPlus />}
            onPress={() => {
              openModal('AdminUserForm', {
                onViewUserProfile,
                testID: buildTestId({
                  testID: TEST_ID,
                  suffix: ['admin', 'user', 'create', 'modal'],
                }),
              });
            }}
          />
        }
      />
      <Outlet />
    </Stack>
  );
}
